
































































































































































// vue
import {
    defineComponent,
    ref,
    onMounted,
    watch,
    onUnmounted,
} from '@vue/composition-api';

// components
import Mapbox from '@/vue3/components/Mapbox.vue';
import CompareMapbox from '@/vue3/components/CompareMapbox.vue';
import Weather from '@/vue3/components/Weather.vue';
import ToolOptions from '@/vue3/components/ToolOptions.vue';
import FeatureArea from '@/vue3/components/FeatureArea.vue';
import ToolSelector from '@/vue3/components/ToolSelector.vue';
import HoleNavigator from '@/vue3/components/HoleNavigator.vue';
import UtilityDrawer from '@/vue3/components/UtilityDrawer.vue';
import GpsUi from '@/vue3/components/geo-record-tools/GpsUi.vue';
import LayerTree from '@/vue3/components/layer-tree/LayerTree.vue';
import GeoRecordFilter from '@/vue3/components/GeoRecordFilter.vue';

// hooks
import { useStore } from '@/store';
import { useMapboxDraw } from '@/vue3/hooks/mapboxDrawHook';

// services
import { useService } from '@/vue3/api/services';

// models
import { MeasureRecord, GeoRecord } from '@/vue3/api/models';

// config
import tools from '@/vue3/configs/toolsConfig';

// mapbox
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

// primeveu
import Button from 'primevue/button';

export default defineComponent({
    components: {
        GpsUi,
        Mapbox,
        Weather,
        LayerTree,
        ToolOptions,
        FeatureArea,
        ToolSelector,
        HoleNavigator,
        UtilityDrawer,
        GeoRecordFilter,
        CompareMapbox,
        Button,
    },
    setup(props: any) {
        // hooks
        const store = useStore();

        const { draw, setDrawMode, setDrawSnap } = useMapboxDraw();

        //services
        const {
            getProjects,
            getAllGeoRecordStatus,
            getGeoRecordCategoryByRole,
            getAllAccountByOrganization,
            getTags,
            getCourseSchema,
            getUserSettings,
            getGeoRecord,
        } = useService();

        // refs
        const gps = ref<InstanceType<typeof GpsUi>>();
        const mapbox = ref<InstanceType<typeof Mapbox>>();
        const layerTree = ref<InstanceType<typeof LayerTree>>();
        const toolOptions = ref<InstanceType<typeof ToolOptions>>();
        const utilityDrawer = ref<InstanceType<typeof UtilityDrawer>>();
        const compareMapbox = ref<InstanceType<typeof CompareMapbox>>();

        const utilityDrawerOpenStateFlag = ref<boolean>(false);

        const filterData = ref<object[]>([]);
        const geoRecords = ref<GeoRecord[]>([]);
        const measureRecords = ref<MeasureRecord[]>([]);

        const accounts = ref();
        const activeTool = ref();
        const activeToolName = ref();
        const activeCreateToolName = ref();
        const zoomedMapBbox = ref();

        const isDrawAdded = ref(false);
        const isComparingMosaic = ref(false);
        const courseExtent = ref<any>({});

        const initSelectGeoRecord = ref<any>({ geoRecordId: 0, isEdit: false });

        let europeTourintervalId;

        // watch
        watch(
            () => activeTool.value,
            (value) => {
                setDrawMode(value.mode);

                if (
                    value.name !== 'CreateTool' &&
                    gps.value &&
                    gps.value.isActive
                ) {
                    gps.value.clearGps();
                }
            },
        );

        //methods
        const initDraw = (map: mapboxgl.Map): void => {
            map.addControl(draw.value);
            isDrawAdded.value = true;
        };

        const setGeoRecords = (data): void => {
            geoRecords.value = data;

            //The following is to check if there's a geo record to be selected as soon as geo records are loaded.
            if (initSelectGeoRecord.value.geoRecordId > 0) {
                try {
                    selectGeoRecordById(
                        initSelectGeoRecord.value.geoRecordId,
                        initSelectGeoRecord.value.isEdit,
                    );
                } catch (e) {
                    console.log(e);
                }
            }
        };

        const setMeasureRecords = (data): void => {
            measureRecords.value = data;
        };

        const setTourIntervalId = (data): void => {
            europeTourintervalId = data;
        };

        const setCourseExtent = (data): void => {
            courseExtent.value = data;
        };

        const setActiveCreateToolName = (name: string): void => {
            activeCreateToolName.value = name;
        };

        const setActiveTool = (name: string): void => {
            activeTool.value = tools.filter((x) => x.name === name)[0];
            activeToolName.value = name;
        };

        const afterLoadCallback = (map: mapboxgl.Map): void => {
            isComparingMosaic.value = !!mapbox.value?.isCompareMosaic;
            map.on('draw.selection_change', function (e) {});
        };

        const afterCompareLoadCallback = (value: boolean): void => {
            isComparingMosaic.value = value;
        };

        const onLoadCallback = (map: mapboxgl.Map): void => {
            if (!isDrawAdded.value) {
                map.addControl(draw.value);
                isDrawAdded.value = true;
            }
            loadFilterData();
        };

        const loadFilterData = async (): Promise<void> => {
            const holes: any[] = [];
            const numHoles = JSON.parse(
                store.state.currentCourseSchema || '',
            ).numHoles;

            if (numHoles > 0) {
                for (let i = 1; i <= numHoles; i++) {
                    holes.push({ hole: i });
                }
            }
            filterData.value['holes'] = holes;
            filterData.value['accounts'] = await getAllAccountByOrganization();
            filterData.value['status'] = await getAllGeoRecordStatus();
            filterData.value['projects'] = await getProjects();
            filterData.value['categories'] = await getGeoRecordCategoryByRole();
            filterData.value['tags'] = await getTags();
        };

        const getBoundingBoxAndZoomLevel = () => {
            if (mapbox.value) {
                mapbox.value.map.on('moveend', getBoundingBoxAndZoomLevel);
                mapbox.value.map.on('zoomend', getBoundingBoxAndZoomLevel);

                let bounds = mapbox.value.map.getBounds();
                zoomedMapBbox.value = [
                    bounds.getWest(),
                    bounds.getSouth(),
                    bounds.getEast(),
                    bounds.getNorth(),
                ];
            }
        };

        const updateCompareMapboxProperty = ({
            isCompareMosaic,
            leftMosaic,
            rightMosaic,
        }) => {
            if (compareMapbox.value) {
                compareMapbox.value.isCompareMosaic = isCompareMosaic;
                compareMapbox.value.leftMosaic = leftMosaic;
                compareMapbox.value.rightMosaic = rightMosaic;
            }
        };

        const updateLayerTreeProperty = (isCompareMosaic: boolean) => {
            if (layerTree.value) {
                layerTree.value.isCompareMosaic = isCompareMosaic;
            }
        };

        const updateUtilityDrawerState = (state: boolean) => {
            utilityDrawerOpenStateFlag.value = state;
        };

        const setMapboxElevation = (state: boolean) => {
            if (mapbox.value) {
                mapbox.value.isElevation = state;
            }
        };

        const updatefilterData = async (filterDataSubObject = {}) => {
            filterData.value = { ...filterData.value, ...filterDataSubObject };
        };

        const selectGeoRecordById = async (
            geoRecordId: number,
            isEdit: boolean = false,
        ) => {
            const geoRecord = await getGeoRecord(geoRecordId);
            if (
                store.getters.currentCourseSchema.id != geoRecord.courseSchemaId
            ) {
                return;
            }
            let geomTypeCamel =
                geoRecord.geometry.type.charAt(0).toLowerCase() +
                geoRecord.geometry.type.slice(1);

            const bbox = turf.bbox(
                turf[geomTypeCamel](geoRecord.geometry.coordinates),
            );

            if (mapbox.value) {
                mapbox.value.map.fitBounds(bbox, {
                    padding: { top: 200, bottom: 200, left: 350, right: 360 },
                    maxZoom: geomTypeCamel === 'point' ? 19 : 24,
                });
            }

            if (
                isEdit &&
                toolOptions.value &&
                toolOptions.value.$refs?.SelectTool
            ) {
                // @ts-ignore
                toolOptions.value.$refs.SelectTool.onGeoRecordClicked(
                    {
                        id: geoRecord.id,
                        geometry: geoRecord.geometry,
                    },
                    true,
                );
            }
        };

        const loadCourseSchema = async () => {
            const baseURL = window.location.href.split('?')[0];
            const fullURL = window.location;

            if (!store.state.courseSchemas) {
                window.location.href = `${baseURL}`;
            }

            const urlParams = new URLSearchParams(fullURL.search);
            const courseIdParam = urlParams.get('courseId');
            const geoRecordIdParam = urlParams.get('geoRecordId');
            const isEditParam = urlParams.get('isEdit');

            if (courseIdParam && courseIdParam.length > 0) {
                const courseSchemas = JSON.parse(
                    (store.state || {}).courseSchemas || '[]',
                );
                const ifFound = courseSchemas.find(
                    (courseSchema: any) =>
                        courseSchema.id == parseInt(courseIdParam),
                );
                if (!ifFound) {
                    window.location.href = `${baseURL}`;
                    return false;
                }

                let courseSchemaData = await getCourseSchema(
                    parseInt(courseIdParam),
                );
                if (!courseSchemaData || !courseSchemaData.id) {
                    window.location.href = `${baseURL}`;
                    return false;
                }
                store.commit('setCurrentCourseSchema', courseSchemaData);
                urlParams.delete('courseId');
                window.location.href = `${baseURL}?${urlParams}`;
                return true;
            } else if (geoRecordIdParam && geoRecordIdParam.length > 0) {
                const geoRecId = parseInt(geoRecordIdParam);
                const isEdit =
                    isEditParam && isEditParam.length > 0
                        ? isEditParam == 'true'
                        : false;
                if (geoRecId > 0) {
                    initSelectGeoRecord.value = {
                        geoRecordId: geoRecId,
                        isEdit,
                    };
                }
                window.history.pushState('Clear Golf Hub', '', `${baseURL}`);
            }
            return false;
        };

        // lifecycle
        onMounted(async () => {
            const isDifferentCourseLoaded = await loadCourseSchema();
            if (isDifferentCourseLoaded) {
                return;
            }
            activeTool.value = tools[0];
            getBoundingBoxAndZoomLevel();
        });

        onUnmounted(() => {
            clearInterval(europeTourintervalId);
        });

        return {
            mapbox,
            setMapboxElevation,
            geoRecords,
            measureRecords,
            activeToolName,
            activeCreateToolName,
            setGeoRecords,
            setMeasureRecords,
            setTourIntervalId,
            tools,
            activeTool,
            setActiveCreateToolName,
            utilityDrawer,
            utilityDrawerOpenStateFlag,
            updateUtilityDrawerState,
            accounts,
            filterData,
            updatefilterData,
            toolOptions,
            layerTree,
            updateLayerTreeProperty,
            setActiveTool,
            initDraw,
            draw,
            setDrawSnap,
            onLoadCallback,
            afterLoadCallback,
            isComparingMosaic,
            compareMapbox,
            updateCompareMapboxProperty,
            afterCompareLoadCallback,
            zoomedMapBbox,
            setCourseExtent,
            courseExtent,
            store,
        };
    },
});
